import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { CardSaveBadge, type CardSavePhase } from '../tenant/CardSaveBadge';
import ClosingDocumentUpload from './ClosingDocumentUpload';
import type { ClosingPersonalState } from './closing-model';
import { CLOSING_CARD } from './closing-styles';

const DEPOSIT_EMAIL = 'rent@amberpeak.ca';

type DepositCopyField = 'amount' | 'memo' | 'address';

function depositMemo(personName: string, propertyLabel: string): string {
  const name = personName.trim() || 'your name';
  const place = propertyLabel.trim();
  if (!place || place === 'Unknown unit') {
    return `Last Month's Rent Deposit for ${name}`;
  }
  return `Last Month's Rent Deposit for ${name} @ ${place}`;
}

/** Bank amount fields want digits, not a currency symbol or thousands separator. */
function plainAmount(label: string): string {
  return label.replace(/[^0-9.]/g, '');
}

function CopyValueButton({
  copied,
  label,
  onCopy,
}: {
  copied: boolean;
  label: string;
  onCopy: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={label}
      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-semibold text-gray-900 hover:bg-gray-50 dark:border-white/15 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
    >
      {copied ? <Check size={12} className="text-emerald-600" aria-hidden /> : <Copy size={12} aria-hidden />}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

interface ClosingETransferStepProps {
  state: ClosingPersonalState;
  personName: string;
  propertyLabel: string;
  disabled?: boolean;
  savePhase?: CardSavePhase;
  complete: boolean;
  file: File | null;
  savedDocumentId?: string | null;
  getSavedDocumentUrl?: (documentId: string) => Promise<string>;
  onFile: (file: File) => void;
  onClear: () => void;
  onDeleteSaved?: () => void;
}

export default function ClosingETransferStep({
  state,
  personName,
  propertyLabel,
  disabled = false,
  savePhase = 'idle',
  complete,
  file,
  savedDocumentId = null,
  getSavedDocumentUrl,
  onFile,
  onClear,
  onDeleteSaved,
}: ClosingETransferStepProps) {
  const memo = depositMemo(personName, propertyLabel);
  const amountLabel = state.eTransferAmountLabel;
  const [copiedField, setCopiedField] = useState<DepositCopyField | null>(null);

  async function copyField(field: DepositCopyField, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      window.setTimeout(() => {
        setCopiedField((current) => (current === field ? null : current));
      }, 2000);
    } catch {
      setCopiedField(null);
    }
  }
  const fileName = state.lastMonthRentETransferFileName;

  const depositTable = (
    <table className="w-full text-sm">
      <tbody className="divide-y divide-gray-200 dark:divide-white/10">
        <tr>
          <th scope="row" className="w-[46%] py-2.5 pr-3 text-left align-top font-normal leading-snug text-gray-600 dark:text-gray-300">
            Amount of e-transfer
          </th>
          <td className="py-2.5 align-top">
            <div className="flex items-start justify-between gap-2">
              <span className="min-w-0 font-semibold tabular-nums text-gray-900 dark:text-white">
                {amountLabel ?? '—'}
              </span>
              {amountLabel ? (
                <CopyValueButton
                  copied={copiedField === 'amount'}
                  label="Copy amount"
                  onCopy={() => void copyField('amount', plainAmount(amountLabel))}
                />
              ) : null}
            </div>
          </td>
        </tr>
        <tr>
          <th scope="row" className="py-2.5 pr-3 text-left align-top font-normal leading-snug text-gray-600 dark:text-gray-300">
            Memo on e-transfer
          </th>
          <td className="py-2.5 align-top">
            <div className="flex items-start justify-between gap-2">
              <span className="min-w-0 leading-snug text-gray-900 dark:text-white">{memo}</span>
              <CopyValueButton
                copied={copiedField === 'memo'}
                label="Copy memo"
                onCopy={() => void copyField('memo', memo)}
              />
            </div>
          </td>
        </tr>
        <tr>
          <th scope="row" className="py-2.5 pr-3 text-left align-top font-normal leading-snug text-gray-600 dark:text-gray-300">
            Address on e-transfer
          </th>
          <td className="py-2.5 align-top">
            <div className="flex items-start justify-between gap-2">
              <a
                href={`mailto:${DEPOSIT_EMAIL}`}
                className="min-w-0 break-all font-medium text-gray-900 underline underline-offset-2 dark:text-white"
              >
                {DEPOSIT_EMAIL}
              </a>
              <CopyValueButton
                copied={copiedField === 'address'}
                label="Copy address"
                onCopy={() => void copyField('address', DEPOSIT_EMAIL)}
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );

  return (
    <section className={`${CLOSING_CARD} flex h-full flex-col`}>
      <CardSaveBadge phase={savePhase} complete={complete} />
      <h2 className="pr-8 text-base font-semibold text-gray-900 dark:text-white">Last Month&apos;s Rent Deposit</h2>
      <ClosingDocumentUpload
        note="Send an e-transfer with these details, then upload a screenshot or PDF of your e-transfer confirmation."
        instructionsExtra={depositTable}
        emptyPreview="Your e-transfer confirmation will appear here."
        previewSheet
        fileName={fileName}
        savedDocumentId={savedDocumentId}
        getSavedDocumentUrl={getSavedDocumentUrl}
        file={file}
        onFile={onFile}
        onClear={onClear}
        onDeleteSaved={onDeleteSaved}
        disabled={disabled || savePhase === 'saving'}
        reviewStatus={{
          review: state.eTransferSentReview,
          hasSubmission: Boolean(fileName),
        }}
      />
    </section>
  );
}
